import { useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import useIsDesktop from "@/hooks/useIsDesktop";
import styles from "./OfferBanner.module.css";

const AUTO_SLIDE_MS = 5000;
const SLIDE_TRANSITION_MS = 550;

function buildContent(item = {}) {
  const title = item.title?.trim() || "";
  const subTitle = item.subTitle?.trim() || "";
  return {
    title,
    subTitle,
    mobileTitle: title,
    mobileSubTitle: subTitle,
    btnText: item.btnText?.trim() || "",
    btnUrl: item.btnUrl?.trim() || "",
  };
}

function hasOfferContent(content) {
  return Boolean(
    content.title || content.subTitle || content.btnText || content.btnUrl
  );
}

function OfferBannerCard({ content, showDesktopButton }) {
  const buttonClass = styles.offerBtn;

  const button =
    showDesktopButton && content.btnText && content.btnUrl ? (
      <Link href={content.btnUrl} className={buttonClass}>
        {content.btnText}
      </Link>
    ) : showDesktopButton && content.btnText ? (
      <button type="button" className={buttonClass}>
        {content.btnText}
      </button>
    ) : null;

  return (
    <section className={styles.offerBanner}>
      <div className={styles.offerLeft}>
        <div className={styles.offerIcon}>
          <img src="/assets/icons/offerbanner.svg" alt="Offer" />
        </div>

        <div className={styles.offerContent}>
          {content.title ? <h2>{content.title}</h2> : null}
          {content.subTitle ? <p>{content.subTitle}</p> : null}
        </div>
        <div className={styles.offerContentMobile}>
          {content.mobileTitle ? <h2>{content.mobileTitle}</h2> : null}
          {content.mobileSubTitle ? <p>{content.mobileSubTitle}</p> : null}
        </div>
      </div>

      {button}
    </section>
  );
}

export default function OfferBanner({
  items: itemsProp,
  title,
  subTitle,
  btnText,
  btnUrl,
}) {
  const router = useRouter();
  const trackRef = useRef(null);
  const positionRef = useRef(1);
  const { isDesktop, mounted } = useIsDesktop(769);
  const [positionIndex, setPositionIndex] = useState(1);
  const [transitionEnabled, setTransitionEnabled] = useState(true);

  const items = useMemo(() => {
    if (Array.isArray(itemsProp) && itemsProp.length) {
      return itemsProp.map((item) => buildContent(item)).filter(hasOfferContent);
    }

    const hasLegacyProps =
      title?.trim() ||
      subTitle?.trim() ||
      btnText?.trim() ||
      btnUrl?.trim();

    if (hasLegacyProps) {
      return [
        buildContent({
          title,
          subTitle,
          btnText,
          btnUrl,
        }),
      ].filter(hasOfferContent);
    }

    return [];
  }, [itemsProp, title, subTitle, btnText, btnUrl]);

  const slideCount = items.length;
  const hasMultiple = slideCount > 1;

  const loopSlides = useMemo(() => {
    if (!hasMultiple) return items;
    return [items[slideCount - 1], ...items, items[0]];
  }, [hasMultiple, items, slideCount]);

  const cloneFirstIndex = slideCount + 1;

  positionRef.current = positionIndex;

  const showDesktopButton = mounted && isDesktop;

  useEffect(() => {
    setPositionIndex(1);
    setTransitionEnabled(true);
  }, [slideCount]);

  useEffect(() => {
    if (!hasMultiple || !mounted) return undefined;

    const timer = window.setInterval(() => {
      setTransitionEnabled(true);
      setPositionIndex((prev) => prev + 1);
    }, AUTO_SLIDE_MS);

    return () => window.clearInterval(timer);
  }, [hasMultiple, mounted, slideCount]);

  useEffect(() => {
    if (transitionEnabled) return undefined;

    const frame = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => setTransitionEnabled(true));
    });

    return () => window.cancelAnimationFrame(frame);
  }, [positionIndex, transitionEnabled]);

  const handleTrackTransitionEnd = (event) => {
    if (event.propertyName !== "transform") return;
    if (event.target !== trackRef.current) return;
    if (!hasMultiple) return;
    if (positionRef.current !== cloneFirstIndex) return;

    setTransitionEnabled(false);
    setPositionIndex(1);
  };

  const navigateToOffer = (url) => {
    if (!url) return;
    router.push(url);
  };

  const renderSlide = (content) => {
    const card = (
      <OfferBannerCard content={content} showDesktopButton={showDesktopButton} />
    );

    const isMobileInteractive =
      mounted && !isDesktop && Boolean(content.btnUrl);

    if (!isMobileInteractive) {
      return card;
    }

    return (
      <div
        className={styles.offerBannerTapTarget}
        role="link"
        tabIndex={0}
        onClick={() => navigateToOffer(content.btnUrl)}
        onKeyDown={(event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          navigateToOffer(content.btnUrl);
        }}
      >
        {card}
      </div>
    );
  };

  const slidesToRender = hasMultiple ? loopSlides : items;

  if (!items.length) return null;

  return (
    <div className="standard-padding">
      <div className={styles.offerViewport}>
        <div
          ref={trackRef}
          className={styles.offerTrack}
          style={
            hasMultiple
              ? {
                  transform: `translate3d(-${positionIndex * 100}%, 0, 0)`,
                  transition: transitionEnabled
                    ? `transform ${SLIDE_TRANSITION_MS}ms ease`
                    : "none",
                }
              : undefined
          }
          onTransitionEnd={handleTrackTransitionEnd}
        >
          {slidesToRender.map((content, index) => (
            <div
              className={styles.offerSlide}
              key={`${content.title}-${content.btnUrl}-${index}`}
            >
              {renderSlide(content)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
